import type {
  CanvasFill,
  CanvasFrame,
  CanvasGroupLayer,
  CanvasItem,
  CanvasLayer,
  CanvasSet,
  CanvasShape,
} from "schemas/generated/canvas/document";
import type {
  CanvasBlockV2,
  CanvasFrameV2,
  CanvasItemV2,
  CanvasLayerV2,
  CanvasSetV2,
} from "./legacy/v2";

import { filterNulls } from "replo-utils/lib/array";
import { parseJsonOrNull } from "replo-utils/lib/json";
import { exhaustiveSwitch } from "replo-utils/lib/misc";
import * as z from "zod";

import {
  CANVAS_FORMAT_VERSION,
  canvasSetSchema,
  CanvasSetUnreadableError,
  emptyCanvasSet,
  sortFramesByGrid,
} from "./document";
import { canvasSetV2Schema } from "./legacy/v2";

/**
 * How to step each on-disk Canvas format up to the next, keyed by the version
 * it upgrades from. A file climbs one step at a time to `CANVAS_FORMAT_VERSION`,
 * then is validated against the live schema.
 *
 * To change the format: freeze the current schema under `legacy/`, bump
 * `CANVAS_FORMAT_VERSION`, and add the step from the old version. Each step
 * parses its input with a frozen schema, so editing the live schema never
 * changes how an old file is read.
 */
const UPGRADE_FROM: Record<number, (document: unknown) => unknown> = {
  // v1 is v2 without `items`; one step reads both.
  2: (document) => v2ToV3(canvasSetV2Schema.parse(document)),
};

const OLDEST_READABLE_VERSION = 2;

const UNREADABLE = "This file has content Canvas can't read.";

/** The format a raw document says it is in: `version` from v3 on, `schemaVersion` (1 or 2) before. */
const versionSchema = z.union([
  z.object({ version: z.number() }).transform(({ version }) => version),
  z.object({ schemaVersion: z.number() }).transform(({ schemaVersion }) => {
    return Math.max(schemaVersion, OLDEST_READABLE_VERSION);
  }),
]);

/**
 * Upgrade a parsed `*.canvas.json` document to the current format.
 * `migratedFrom` is the version it arrived in, or null when it was current, so
 * a caller that owns the file knows to write the upgrade back.
 */
export function migrateCanvasSet(raw: unknown): {
  set: CanvasSet;
  migratedFrom: number | null;
  /** Ids the file repeated were renamed; the caller that owns the file writes the repair back. */
  repaired: boolean;
} {
  const version = versionSchema.safeParse(raw).data ?? null;
  if (version === null || version < OLDEST_READABLE_VERSION) {
    throw new CanvasSetUnreadableError({ message: UNREADABLE });
  }
  if (version > CANVAS_FORMAT_VERSION) {
    throw new CanvasSetUnreadableError({
      message: "This set was saved by a newer version of Canvas.",
    });
  }
  let document = raw;
  try {
    for (let from = version; from < CANVAS_FORMAT_VERSION; from += 1) {
      const upgrade = UPGRADE_FROM[from];
      if (!upgrade) {
        throw new CanvasSetUnreadableError({
          message: `No upgrade from v${from}`,
        });
      }
      document = upgrade(document);
    }
  } catch (error) {
    throw new CanvasSetUnreadableError({ message: UNREADABLE, cause: error });
  }
  const parsed = canvasSetSchema.safeParse(document);
  if (!parsed.success) {
    throw new CanvasSetUnreadableError({
      message: UNREADABLE,
      additionalData: { issues: parsed.error.issues.slice(0, 5) },
    });
  }
  const { set, repaired } = withUniqueIds(parsed.data);
  return {
    set,
    migratedFrom: version === CANVAS_FORMAT_VERSION ? null : version,
    repaired,
  };
}

/**
 * Every save is refused while two frames or two layers share an id, so a file
 * that repeats one (an agent copying a frame's JSON to make another size) is
 * repaired on read: later copies get a fresh suffix and the first keeps its id,
 * so references to it still land.
 */
function withUniqueIds(set: CanvasSet): { set: CanvasSet; repaired: boolean } {
  let repaired = false;
  const uniquer = () => {
    const taken = new Set<string>();
    return (id: string) => {
      let fresh = id;
      for (let suffix = 2; taken.has(fresh); suffix += 1) {
        fresh = `${id}-${suffix}`;
      }
      repaired ||= fresh !== id;
      taken.add(fresh);
      return fresh;
    };
  };
  const frameId = uniquer();
  const layerId = uniquer();
  const uniqueLayers = (layers: CanvasLayer[]): CanvasLayer[] => {
    return layers.map((layer) => {
      const id = layerId(layer.id);
      if (layer.kind === "group") {
        return { ...layer, id, children: uniqueLayers(layer.children) };
      }
      return { ...layer, id };
    });
  };
  const frames = set.frames.map((frame) => {
    return {
      ...frame,
      id: frameId(frame.id),
      layers: uniqueLayers(frame.layers),
    };
  });
  return repaired ? { set: { ...set, frames }, repaired } : { set, repaired };
}

/** Parse a `*.canvas.json` body. An empty body is a set whose first write never landed. */
export function parseCanvasSetText(
  text: string,
): ReturnType<typeof migrateCanvasSet> {
  if (!text.trim()) {
    return { set: emptyCanvasSet(), migratedFrom: null, repaired: false };
  }
  return migrateCanvasSet(parseJsonOrNull(text));
}

/* -------------------------------------------------------------------------- */
/* v2 → v3                                                                    */
/* -------------------------------------------------------------------------- */

/** A layer's painted size in frame pixels, which Fabric's pixel-unit gradients are measured against. */
type PixelSize = { width: number; height: number };

/** v2 stored `shadow: true` for the one shadow the editor offered. */
const V2_SHADOW = { color: "rgba(0,0,0,.35)", x: 0, y: 24, blur: 40 };

/** v2 frames without rows were packed this many to a row. */
const V2_ROW_MAX_FRAMES = 6;

function v2ToV3(set: CanvasSetV2): CanvasSet {
  return {
    version: 3,
    frames: sortFramesByGrid(
      withGrid(set.frames).map(({ frame, row, col }) => {
        return frameToV3(frame, row, col);
      }),
    ),
    items: (set.items ?? []).map(itemToV3),
    coverFrameId: set.thumbnailFrameId,
  };
}

/**
 * v2 placed frames by `row`/`col` when it had them and by drawn `x`/`y` before
 * that. A set read off coordinates bands frames into rows by their tops; a
 * frame with neither joins the end, six to a row.
 */
function withGrid(frames: CanvasFrameV2[]) {
  if (
    frames.every((frame) => frame.row !== undefined && frame.col !== undefined)
  ) {
    return frames.map((frame) => ({
      frame,
      row: frame.row ?? 0,
      col: frame.col ?? 0,
    }));
  }
  const placed = frames
    .filter((frame) => frame.x !== undefined && frame.y !== undefined)
    .sort((left, right) => {
      return (left.y ?? 0) - (right.y ?? 0) || (left.x ?? 0) - (right.x ?? 0);
    });
  const rows: CanvasFrameV2[][] = [];
  let bandBottom = -Infinity;
  for (const frame of placed) {
    const top = frame.y ?? 0;
    const current = rows.at(-1);
    if (current && top < bandBottom) {
      current.push(frame);
      bandBottom = Math.max(bandBottom, top + frame.format.h);
    } else {
      rows.push([frame]);
      bandBottom = top + frame.format.h;
    }
  }
  for (const row of rows) {
    row.sort((left, right) => (left.x ?? 0) - (right.x ?? 0));
  }
  for (const frame of frames.filter((entry) => !placed.includes(entry))) {
    const last = rows.at(-1);
    if (last && last.length < V2_ROW_MAX_FRAMES) {
      last.push(frame);
    } else {
      rows.push([frame]);
    }
  }
  return rows.flatMap((row, rowIndex) => {
    return row.map((frame, col) => ({ frame, row: rowIndex, col }));
  });
}

function frameToV3(
  frame: CanvasFrameV2,
  row: number,
  col: number,
): CanvasFrame {
  const size = { width: frame.format.w, height: frame.format.h };
  return {
    id: frame.id,
    name: frame.name,
    row,
    col,
    size: frame.format,
    safeArea: frame.safe,
    // v2 painted "" as no fill at all; v3 says so with "transparent", since null is white.
    background:
      frame.background === null
        ? null
        : (fillToV3(frame.background, size) ?? "transparent"),
    layers: layersToV3(frame.layers, size),
    ...(frame.templateId ? { templateId: frame.templateId } : {}),
    // v2 marked a Sightglass reference ad by its size label alone.
    ...(frame.format.label === "Source ad"
      ? { role: "reference" as const }
      : {}),
    ...(frame.generation ? { generation: frame.generation } : {}),
  };
}

function layersToV3(
  layers: CanvasLayerV2[],
  parentPx: PixelSize,
): CanvasLayer[] {
  return filterNulls(layers.map((layer) => layerToV3(layer, parentPx)));
}

/** A layer as v3 has it, or null for one v3 cannot hold: a picture with no image behind it, or a group left empty. */
function layerToV3(
  layer: CanvasLayerV2,
  parentPx: PixelSize,
): CanvasLayer | null {
  const [, , boxW = 0, boxH = 0] = layer.box;
  const px = { width: boxW * parentPx.width, height: boxH * parentPx.height };
  const base = {
    id: layer.id,
    ...(layer.name !== undefined ? { name: layer.name } : {}),
    ...(layer.slot !== undefined ? { slot: layer.slot } : {}),
    box: layer.box,
    ...(layer.rotation ? { rotation: layer.rotation } : {}),
    ...(layer.skewX ? { skewX: layer.skewX } : {}),
    ...(layer.opacity !== undefined && layer.opacity !== 1
      ? { opacity: Math.min(1, Math.max(0, layer.opacity)) }
      : {}),
    ...(layer.locked ? { locked: true } : {}),
    ...(layer.hidden ? { hidden: true } : {}),
    ...(layer.flipX ? { flipX: true } : {}),
    ...(layer.flipY ? { flipY: true } : {}),
  };
  return exhaustiveSwitch(layer.kind)<CanvasLayer | null>({
    photo: () => imageToV3(layer, base),
    image: () => imageToV3(layer, base),
    logo: () => imageToV3(layer, base),
    svg: () => imageToV3(layer, base),
    text: () => textToV3(layer, base, px),
    shape: () => shapeToV3(layer, base, px),
    group: () => {
      const children = layersToV3(layer.children ?? [], px);
      if (children.length === 0) {
        return null;
      }
      return {
        ...base,
        kind: "group",
        ...(layer.groupSize ? { intrinsicSize: layer.groupSize } : {}),
        children,
      };
    },
  });
}

type V3Base = Omit<CanvasGroupLayer, "kind" | "children" | "intrinsicSize">;

function imageToV3(layer: CanvasLayerV2, base: V3Base): CanvasLayer | null {
  if (!layer.ref) {
    return null;
  }
  // v2 re-covered a picture that was the photo, was asked to cover, or had never been cropped; any other crop it stretched to the box.
  const fit =
    layer.fit ??
    (layer.slot === "photo" || layer.crop === undefined ? "cover" : "fill");
  // v2 also said "this is the logo" with the kind; v3 says it with the slot alone.
  const slot = base.slot ?? (layer.kind === "logo" ? "logo" : undefined);
  return {
    ...base,
    ...(slot ? { slot } : {}),
    kind: "image",
    ref: layer.ref,
    fit,
    ...(layer.crop ? { crop: layer.crop } : {}),
    ...(layer.radius && layer.radius > 0 ? { radius: layer.radius } : {}),
    ...(layer.shadow ? { shadow: V2_SHADOW } : {}),
  };
}

function textToV3(
  layer: CanvasLayerV2,
  base: V3Base,
  px: PixelSize,
): CanvasLayer {
  const text = layer.text ?? "";
  const fit =
    layer.size?.fit ??
    (["headline", "subline"].includes(layer.slot ?? "") ? "shrink" : "wrap");
  const runs = stylesToRuns(text, layer.styles);
  const strokeWidth = positive(layer.strokeWidth);
  return {
    ...base,
    kind: "text",
    text,
    font: layer.font || "Inter",
    ...(layer.weight !== undefined && layer.weight !== 400
      ? { weight: layer.weight }
      : {}),
    fontSize: positive(layer.size?.value) ?? 40,
    color:
      layer.fill === undefined
        ? "#ffffff"
        : (fillToV3(layer.fill, px) ?? "transparent"),
    ...(layer.italic ? { italic: true } : {}),
    ...(layer.underline ? { underline: true } : {}),
    ...(layer.align && layer.align !== "left" ? { align: layer.align } : {}),
    ...(positive(layer.lineHeight) !== undefined && layer.lineHeight !== 1
      ? { lineHeight: layer.lineHeight }
      : {}),
    // v2 kept Fabric's charSpacing, thousandths of an em.
    ...(layer.tracking ? { letterSpacing: layer.tracking / 1000 } : {}),
    ...(layer.wrap
      ? {
          paragraph: {
            height: positive(layer.frameH) ?? positive(layer.box[3]) ?? 0.1,
            fit: fit === "wrap" ? ("grow" as const) : fit,
            ...(positive(layer.size?.max) !== undefined
              ? { maxFontSize: layer.size?.max }
              : {}),
          },
        }
      : {}),
    ...(layer.textPath ? { path: layer.textPath } : {}),
    ...(runs.length ? { runs } : {}),
    ...(layer.textBackgroundColor
      ? { highlight: { color: layer.textBackgroundColor } }
      : {}),
    ...(layer.stroke && strokeWidth !== undefined
      ? { stroke: { color: layer.stroke, width: strokeWidth } }
      : {}),
    ...(layer.shadow ? { shadow: V2_SHADOW } : {}),
  };
}

/** v2 accepted any number where v3 needs a positive one; a non-positive value is treated as absent. */
function positive(value: number | undefined): number | undefined {
  return value !== undefined && value > 0 ? value : undefined;
}

function shapeToV3(
  layer: CanvasLayerV2,
  base: V3Base,
  px: PixelSize,
): CanvasLayer {
  const geometry = layer.geometry;
  const fillRule = layer.fillRule ? { fillRule: layer.fillRule } : {};
  const vector: CanvasShape | null =
    geometry?.type === "path"
      ? { type: "path", commands: geometry.commands, ...fillRule }
      : null;
  const polygon: CanvasShape | null =
    geometry?.type === "polygon"
      ? { type: "polygon", points: geometry.points, ...fillRule }
      : null;
  const shape: CanvasShape =
    vector ??
    polygon ??
    exhaustiveSwitch(layer.shape ?? "rect")<CanvasShape>({
      rect: () => ({
        type: "rect" as const,
        ...(layer.radius && layer.radius > 0 ? { radius: layer.radius } : {}),
      }),
      ellipse: () => ({ type: "ellipse" as const }),
      line: () => ({ type: "line" as const }),
    });
  const fill =
    layer.fill !== undefined && layer.shape !== "line"
      ? fillToV3(layer.fill, px)
      : undefined;
  const strokeWidth = positive(layer.strokeWidth);
  return {
    ...base,
    kind: "shape",
    shape,
    ...(fill ? { fill } : {}),
    ...(layer.stroke && strokeWidth !== undefined
      ? {
          stroke: {
            color: layer.stroke,
            width: strokeWidth,
            ...(layer.strokeLineCap ? { cap: layer.strokeLineCap } : {}),
            ...(layer.strokeLineJoin ? { join: layer.strokeLineJoin } : {}),
          },
        }
      : {}),
  };
}

const fabricGradientSchema = z.object({
  type: z.string().optional(),
  gradientUnits: z.string().optional(),
  coords: z.record(z.string(), z.number()).optional(),
  colorStops: z
    .array(
      z.object({
        offset: z.number(),
        color: z.string(),
        opacity: z.number().optional(),
      }),
    )
    .optional(),
});

/**
 * v2 stored gradients as Fabric's own record: `coords` in either box fractions
 * (`gradientUnits: "percentage"`) or pixels of the object (Fabric's default).
 */
function fillToV3(
  fill: NonNullable<CanvasLayerV2["fill"]>,
  px: PixelSize,
): CanvasFill | undefined {
  // v2 wrote "" for "no fill", which v3 expresses by leaving the fill out.
  if (typeof fill === "string") {
    return fill || undefined;
  }
  const record = fabricGradientSchema.parse(fill);
  const isPixels = record.gradientUnits !== "percentage";
  const across = (value: number | undefined, extent: number) => {
    return isPixels ? (value ?? 0) / (extent || 1) : (value ?? 0);
  };
  const coords = record.coords ?? {};
  const stops = (record.colorStops ?? [])
    .map((stop) => ({
      offset: Math.min(1, Math.max(0, stop.offset)),
      color: withAlpha(stop.color, stop.opacity ?? 1),
    }))
    .sort((left, right) => left.offset - right.offset);
  const [first] = stops;
  if (!first) {
    return undefined;
  }
  // A one-stop gradient painted as a flat colour; v3 needs two stops to say the same.
  if (stops.length === 1) {
    stops.push({ ...first, offset: 1 });
  }
  if (record.type === "radial") {
    // Fabric's focal circle (`x1`, `y1`, `r1`) is never authored; the outer circle is the gradient.
    return {
      type: "radial",
      center: [across(coords.x2, px.width), across(coords.y2, px.height)],
      radius: Math.abs(across(coords.r2, px.width)) || 0.5,
      stops,
    };
  }
  return {
    type: "linear",
    from: [across(coords.x1, px.width), across(coords.y1, px.height)],
    to: [across(coords.x2, px.width), across(coords.y2, px.height)],
    stops,
  };
}

/** A hex colour with `opacity` folded into its alpha; Fabric never painted a stop's separate opacity, so other notations stand. */
function withAlpha(color: string, opacity: number): string {
  const hex = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(color)?.[1];
  if (!hex || opacity >= 1) {
    return color;
  }
  const full =
    hex.length === 3 ? [...hex].map((digit) => digit + digit).join("") : hex;
  const alpha = Math.round(Math.max(0, opacity) * 255)
    .toString(16)
    .padStart(2, "0");
  return `#${full}${alpha}`;
}

const V2_STYLE_KEYS: Record<string, string> = {
  fill: "color",
  fontFamily: "font",
  fontWeight: "weight",
  fontSize: "fontSize",
  underline: "underline",
  linethrough: "strikethrough",
};

/**
 * Fabric keyed per-character styles by line and grapheme (`styles[line][char]`),
 * which shifted whenever the text did. v3 keys them by offset into `text`, with
 * neighbouring characters that share a style merged into one run.
 */
function stylesToRuns(
  text: string,
  styles: CanvasLayerV2["styles"],
): NonNullable<Extract<CanvasLayer, { kind: "text" }>["runs"]> {
  if (!styles) {
    return [];
  }
  type Run = { start: number; end: number; style: Record<string, unknown> };
  const runs: Run[] = [];
  let offset = 0;
  text.split("\n").forEach((line, lineIndex) => {
    Array.from(line).forEach((grapheme, charIndex) => {
      const source = styles[String(lineIndex)]?.[String(charIndex)] ?? {};
      const style: Record<string, unknown> = {};
      for (const [key, value] of Object.entries(source)) {
        if (key === "fontStyle") {
          if (value === "italic") {
            style.italic = true;
          }
          continue;
        }
        const target = V2_STYLE_KEYS[key];
        if (
          target === "fontSize" &&
          !(typeof value === "number" && value > 0)
        ) {
          continue;
        }
        if (target && value !== undefined && value !== null && value !== "") {
          style[target] = key === "fontWeight" ? Number(value) || 400 : value;
        }
      }
      const end = offset + grapheme.length;
      const previous = runs.at(-1);
      if (Object.keys(style).length > 0) {
        if (
          previous &&
          previous.end === offset &&
          JSON.stringify(previous.style) === JSON.stringify(style)
        ) {
          previous.end = end;
        } else {
          runs.push({ start: offset, end, style });
        }
      }
      offset = end;
    });
    // The newline itself.
    offset += 1;
  });
  return runs.map(({ start, end, style }) => ({ start, end, ...style }));
}

function itemToV3(item: CanvasItemV2): CanvasItem {
  return exhaustiveSwitch(
    item,
    "kind",
  )<CanvasItem>({
    step: ({ blocks, html, multi, ...rest }) => {
      if (html !== undefined) {
        return { ...rest, html, ...(multi ? { multi } : {}) };
      }
      // Pieces become markup once; pick-any is carried by the checkbox inputs from then on.
      return { ...rest, html: blocksToHtml({ blocks, multi: multi ?? false }) };
    },
    edge: ({ fromBlock, ...rest }) => ({
      ...rest,
      ...(fromBlock !== undefined ? { fromElement: fromBlock } : {}),
    }),
    section: (section) => section,
    page: ({ height, ...rest }) => ({
      ...rest,
      ...(height ? { height: height.value } : {}),
    }),
  });
}

function escapeHtml(text: string): string {
  return text
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;");
}

/** The markup the v2 board drew for a step's pieces, so a converted screen looks the same. */
function blocksToHtml({
  blocks,
  multi,
}: {
  blocks: CanvasBlockV2[];
  multi: boolean;
}): string {
  let hasTitle = false;
  return blocks
    .map((block) => {
      const id = `data-id="${escapeHtml(block.id)}"`;
      return exhaustiveSwitch(
        block,
        "kind",
      )({
        text: ({ text }) => {
          if (!hasTitle) {
            hasTitle = true;
            return `<h2>${escapeHtml(text)}</h2>`;
          }
          return `<p>${escapeHtml(text)}</p>`;
        },
        choice: ({ text }) => {
          return `<label class="choice" ${id}><input type="${multi ? "checkbox" : "radio"}"><span>${escapeHtml(text)}</span></label>`;
        },
        button: ({ text, url }) => {
          return url
            ? `<a class="button" href="${escapeHtml(url)}" ${id}>${escapeHtml(text)}</a>`
            : `<button ${id}>${escapeHtml(text)}</button>`;
        },
        note: ({ text }) => `<aside class="note">${escapeHtml(text)}</aside>`,
        media: ({ text, ref, video }) => {
          const file =
            ref?.source === "file"
              ? ` data-file-id="${escapeHtml(ref.id)}"`
              : "";
          const flag = video ? ' data-video="true"' : "";
          return `<figure class="media"${flag}><img alt="${escapeHtml(text)}"${file}${flag}><figcaption>${escapeHtml(text || (video ? "video" : "image"))}</figcaption></figure>`;
        },
      });
    })
    .join("\n");
}
